import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import useApiClient from '../services/api';
import LanguageSelect from '../components/LanguageSelect';

const ProjectCreate = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const apiClient = useApiClient();
  const [loading, setLoading] = useState(false);
  const [previousProjects, setPreviousProjects] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    clientName: '',
    translatorName: '',
    country: '',
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
    sourceLanguage: 'fr',
    targetLanguage: 'en'
  });

  // Fetch previous projects on component mount
  useEffect(() => {
    const fetchPreviousProjects = async () => {
      try {
        const response = await apiClient.get('/projects');
        setPreviousProjects(response.data.projects || response.data || []);
      } catch (error) {
        console.error('Error fetching previous projects:', error);
      }
    };

    fetchPreviousProjects();
  }, [apiClient]);

  // Calculate due date based on project duration
  const calculateDueDateFromDuration = (createdAt, originalDueDate) => {
    const created = new Date(createdAt);
    const originalDue = new Date(originalDueDate);
    const durationMs = originalDue.getTime() - created.getTime();
    
    const newDueDate = new Date(Date.now() + durationMs);
    return newDueDate.toISOString().split('T')[0];
  };

  // Handle template selection from previous projects
  const handleTemplateSelect = (e) => {
    const projectId = e.target.value;
    setSelectedTemplate(projectId);
    
    if (projectId) {
      const selectedProject = previousProjects.find(p => p.id === projectId);
      if (selectedProject) {
        const newDueDate = calculateDueDateFromDuration(
          selectedProject.createdAt, 
          selectedProject.dueDate
        );
        
        setFormData(prev => ({
          ...prev,
          name: selectedProject.name || '',
          description: selectedProject.description || '',
          clientName: selectedProject.clientName || '',
          translatorName: selectedProject.translatorName || '',
          country: selectedProject.country || '',
          dueDate: newDueDate,
          sourceLanguage: selectedProject.sourceLanguage || 'fr',
          targetLanguage: selectedProject.targetLanguage || 'en'
        }));
      }
    } else {
      // Reset to defaults when no template is selected
      setFormData(prev => ({
        ...prev,
        name: '',
        description: '',
        clientName: '',
        translatorName: '',
        country: '',
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        sourceLanguage: 'fr',
        targetLanguage: 'en'
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Use the POST /projects endpoint
      await apiClient.post('/projects', {
        name: formData.name,
        description: formData.description,
        clientName: formData.clientName,
        translatorName: formData.translatorName,
        country: formData.country,
        dueDate: formData.dueDate,
        sourceLanguage: formData.sourceLanguage,
        targetLanguage: formData.targetLanguage
      });
      
      toast.success(t('projectCreate.messages.createSuccess'));
      navigate('/translator/dashboard');
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error(t('projectCreate.messages.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/translator/dashboard');
  };

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-800 mb-2">{t('projectCreate.title')}</h1>
        <p className="text-gray-600">{t('projectCreate.subtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Template Selection */}
        {previousProjects.length > 0 && (
          <div>
            <label htmlFor="template" className="block text-sm font-medium text-gray-700 mb-2">
              {t('projectCreate.templateLabel')}
            </label>
            <select
              id="template"
              value={selectedTemplate}
              onChange={handleTemplateSelect}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{t('projectCreate.templatePlaceholder')}</option>
              {previousProjects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name} ({project.country}, {project.sourceLanguage?.toUpperCase()} → {project.targetLanguage?.toUpperCase()})
                </option>
              ))}
            </select>
            {selectedTemplate && (
              <p className="text-xs text-blue-600 mt-1">
                {t('projectCreate.templateCopied')}
              </p>
            )}
          </div>
        )}

        {/* Project Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            {t('projectCreate.projectName')} *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={t('projectCreate.projectNamePlaceholder')}
          />
        </div>

        {/* Project Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            {t('projectCreate.description')}
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={t('projectCreate.descriptionPlaceholder')}
          />
        </div>

        {/* Client and Translator Metadata */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="clientName" className="block text-sm font-medium text-gray-700 mb-2">
              {t('projectCreate.clientName')}
            </label>
            <input
              type="text"
              id="clientName"
              name="clientName"
              value={formData.clientName}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('projectCreate.clientNamePlaceholder')}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('projectCreate.clientNameHint')}
            </p>
          </div>
          <div>
            <label htmlFor="translatorName" className="block text-sm font-medium text-gray-700 mb-2">
              {t('projectCreate.translatorName')}
            </label>
            <input
              type="text"
              id="translatorName"
              name="translatorName"
              value={formData.translatorName}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('projectCreate.translatorNamePlaceholder')}
            />
          </div>
        </div>

        {/* Country */}
        <div>
          <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
            {t('projectCreate.country')}
          </label>
          <select
            id="country"
            name="country"
            value={formData.country}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">{t('projectCreate.selectCountry')}</option>
            <option value="France">France</option>
            <option value="United States">United States</option>
            <option value="United Kingdom">United Kingdom</option>
            <option value="Germany">Germany</option>
            <option value="Spain">Spain</option>
            <option value="Italy">Italy</option>
            <option value="Canada">Canada</option>
            <option value="Australia">Australia</option>
            <option value="Netherlands">Netherlands</option>
            <option value="Belgium">Belgium</option>
            <option value="Switzerland">Switzerland</option>
            <option value="Other">Other</option>
          </select>
        </div>

        {/* Due Date */}
        <div>
          <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-2">
            {t('projectCreate.dueDate')}
          </label>
          <input
            type="date"
            id="dueDate"
            name="dueDate"
            value={formData.dueDate}
            onChange={handleInputChange}
            min={new Date().toISOString().split('T')[0]}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Language Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Source Language */}
          <div>
            <label htmlFor="sourceLanguage" className="block text-sm font-medium text-gray-700 mb-2">
              {t('projectCreate.sourceLanguage')}
            </label>
            <LanguageSelect
              id="sourceLanguage"
              name="sourceLanguage"
              value={formData.sourceLanguage}
              onChange={handleInputChange}
              grouped={true}
              placeholder="Select source language"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Target Language */}
          <div>
            <label htmlFor="targetLanguage" className="block text-sm font-medium text-gray-700 mb-2">
              {t('projectCreate.targetLanguage')}
            </label>
            <LanguageSelect
              id="targetLanguage"
              name="targetLanguage"
              value={formData.targetLanguage}
              onChange={handleInputChange}
              grouped={true}
              placeholder="Select target language"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4 pt-4">
          <button
            type="button"
            onClick={handleCancel}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            {t('projectCreate.cancelButton')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-green-300 rounded-lg hover:bg-green-400 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                {t('projectCreate.creating')}
              </>
            ) : (
              <>
                <span>📁</span>
                {t('projectCreate.createButton')}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProjectCreate;
